import { HTTP_INTERCEPTORS, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Provider } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay, dematerialize, materialize, mergeMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { mockMessagingRes } from 'src/shared/mocks/messaging.mock-response';

import { SubUrlsEnum } from '../../shared/enums/sub-urls.enum';
import { mockAuthRes } from '../../shared/mocks/auth.mock-response';
import {
    mockHomeworkSettingsResponse,
    mockMealResponse,
    mockNurseryResponse,
    mockWalletResponse,
} from '../../shared/mocks/boutique.mock-response';
import { mockClassesRes } from '../../shared/mocks/classes.mock-response';
import { mockCloudRes } from '../../shared/mocks/cloud.mock-response';
import { mockGroupsRes } from '../../shared/mocks/groups.mock-response';
import { mockSchoolYearsRes } from '../../shared/mocks/school-years.mock-response';
import { mockSchoolsRes } from '../../shared/mocks/schools.mock-response';
import { mockUsersRes } from '../../shared/mocks/users.mock-response';

@Injectable()
export class MockInterceptor implements HttpInterceptor {
    public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
        const mockEnv: any = environment.mock;
        // wrap in delayed observable to simulate server api call
        return of(null)
            .pipe(
                mergeMap(() => {
                    /* AUTH */
                    if (mockEnv.all || (mockEnv.services && mockEnv.services.auth)) {
                        if (
                            request.url.endsWith(SubUrlsEnum.LOGIN) ||
                            request.url.endsWith(SubUrlsEnum.REFRESH_TOKEN) ||
                            request.url.endsWith(SubUrlsEnum.RESET_PWD) ||
                            request.url.endsWith(SubUrlsEnum.LOGOUT)
                        ) {
                            return mockAuthRes(request);
                        }
                    }
                    /* MESSAGING */
                    if (mockEnv.all || (mockEnv.services && mockEnv.services.messaging)) {
                        if (
                            request.url.endsWith(SubUrlsEnum.MESSAGES) ||
                            request.url.endsWith(SubUrlsEnum.MESSAGE) ||
                            request.url.endsWith(SubUrlsEnum.MESSAGE_VIEWED) ||
                            request.url.endsWith(SubUrlsEnum.MESSAGE_ARCHIVED)
                        ) {
                            return mockMessagingRes(request);
                        }
                    }
                    /* USERS */
                    if (mockEnv.all || (mockEnv.services && mockEnv.services.users)) {
                        if (
                            request.url.endsWith(SubUrlsEnum.USERS) ||
                            (request.url.includes('/users/') &&
                                !request.url.endsWith(SubUrlsEnum.LOGIN) &&
                                !request.url.endsWith(SubUrlsEnum.MYUSER) &&
                                !request.url.endsWith(SubUrlsEnum.REFRESH_TOKEN))
                        ) {
                            return mockUsersRes(request);
                        }
                    }

                    /* GROUPS */
                    if (mockEnv.all || (mockEnv.services && mockEnv.services.groups)) {
                        if (request.url.endsWith(SubUrlsEnum.GROUPS) || request.url.includes(SubUrlsEnum.GROUP)) {
                            return mockGroupsRes(request);
                        }
                    }

                    /* CLASSES */
                    if (mockEnv.all || (mockEnv.services && mockEnv.services.classes)) {
                        if (request.url.endsWith(SubUrlsEnum.CLASSES) || request.url.includes(SubUrlsEnum.CLASS)) {
                            return mockClassesRes(request);
                        }
                    }

                    /* SCHOOLS */
                    if (mockEnv.all || (mockEnv.services && mockEnv.services.schools)) {
                        if (request.url.endsWith(SubUrlsEnum.SCHOOLS) || request.url.includes(SubUrlsEnum.SCHOOL)) {
                            return mockSchoolsRes(request);
                        }
                    }

                    /* SCHOOL-YEARS */
                    if (mockEnv.all || (mockEnv.services && mockEnv.services.schoolYears)) {
                        if (request.url.endsWith(SubUrlsEnum.SCHOOL_YEARS)) {
                            return mockSchoolYearsRes(request);
                        }
                    }

                    /* BOUTIQUE */
                    if (mockEnv.all || (mockEnv.services && mockEnv.services.boutique)) {
                        if (
                            // request.url.endsWith(SubUrlsEnum.WALLET) ||
                            // request.url.endsWith(SubUrlsEnum.WALLET_PAY) ||
                            request.url.endsWith(SubUrlsEnum.WALLET_CHARGE) ||
                            request.url.endsWith(SubUrlsEnum.WALLET_WITHDRAW) ||
                            request.url.endsWith(SubUrlsEnum.TRANSACTIONS)
                        ) {
                            return mockWalletResponse(request);
                        }
                        if (request.url.endsWith(SubUrlsEnum.NURSERY) || request.url.endsWith(SubUrlsEnum.NURSERY_ADD)) {
                            return mockNurseryResponse(request);
                        }
                        if (request.url.endsWith(SubUrlsEnum.MEAL_ORDERS)) {
                            return mockMealResponse(request);
                        }
                        if (request.url.endsWith(SubUrlsEnum.HOMEWORK_SETTINGS)) {
                            return mockHomeworkSettingsResponse(request);
                        }
                    }

                    /* CLOUD */
                    if (mockEnv.all || (mockEnv.services && mockEnv.services.cloud)) {
                        if (
                            request.url.endsWith('/' + SubUrlsEnum.BASE_FOLDERS) ||
                            request.url.includes('/' + SubUrlsEnum.BASE_FOLDERS + '/')
                        ) {
                            return mockCloudRes(request);
                        }
                    }

                    // pass through any requests not handled above
                    // console.log(request.url, 'real service');
                    return next.handle(request);
                })
            )
            .pipe(
                // call materialize and dematerialize to ensure delay even if an error is thrown
                // (cfr: https://github.com/Reactive-Extensions/RxJS/issues/648)
                materialize(),
                delay(200),
                dematerialize()
            );
    }
}

export const mockInterceptorProvider: Provider = {
    provide: HTTP_INTERCEPTORS,
    useClass: MockInterceptor,
    multi: true,
};
